<template>
	<v-navigation-drawer
		v-model="drawer"
		v-if="isAuthenticated"
		:theme-on="drawerTheme"
		:expand-on-hover="hover && drawer && mini"
		:rail="mini"
		:permanent="display.smAndUp.value"
		:temporary="display.xs.value"
		:width="isAuthenticated ? 270 : 180"
		class="nav"
		color="nav"
		data-tour="navDrawer"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
		@touchstart="onMouseEnter"
	>
		<v-list
			data-tour="navMenu"
			nav
			density="compact"
			:items="navItemsFormatted"
			:item-props="itemProps"
			color="secondary"
			base-color="primary"
		>
			<template #prepend>
				<v-icon size="x-small"></v-icon>
			</template>
			<template #append>
				<v-icon size="x-small"></v-icon>
			</template>
		</v-list>

		<template v-if="(drawer && !mini) || hover" #append>
			<BaseImage :src="logo" max-height="20vh" />

			<v-container fluid class="text-center">
				<v-row no-gutters>
					<v-col :cols="5">
						<v-btn
							href="https://www.aareon.de/Impressum.959318.html"
							target="_blank"
							variant="text"
							size="x-small"
						>
							{{ t('component.drawer.imprint') }}
						</v-btn>
					</v-col>
					<v-col :cols="7">
						<v-btn
							:to="{ name: 'privacy' }"
							data-tour="navPrivacyTos"
							variant="text"
							size="x-small"
						>
							{{ t('component.drawer.termsAndPrivacy', { amp: '&amp;' }) }}
						</v-btn>
					</v-col>
					<v-col v-if="isAdmin || isTeam" cols="12">
						<v-btn
							:href="patchNotesUrl"
							target="_blank"
							rel="noopener"
							variant="text"
							size="x-small"
						>
							{{ t('common.releaseNote.name') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-container>

			<v-spacer />
		</template>
	</v-navigation-drawer>
	<Toolbar v-model:drawer="drawer" v-model:mini="mini" />
</template>

<script setup lang="ts">
import { useSubscription } from '@/composables/subscription'
import { patchNotesUrl } from '@/helpers/patchNotesUrl'
import { useLightOrDark } from '@/composables/useLightOrDark'
import { useSettingStore } from '@/stores/modules/setting'
import { useNavStore, useThemeStore, useUserStore } from '@/stores/system'
import { useCookies } from '@vueuse/integrations/useCookies'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, type RouteRecordRaw } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import Toolbar from './Toolbar.vue'
import BaseImage from '../templates/BaseImage.vue'

const { t } = useI18n()
const { logo } = storeToRefs(useThemeStore())
const route = useRoute()
const cookies = useCookies()
const display = useDisplay()
const theme = useTheme()

const {
	isAdmin,
	isTeam,
	isLoggedIn: isAuthenticated,
	isOnline,
} = storeToRefs(useUserStore())

const drawer = ref(true)
const hover = ref(false)

const { nav } = storeToRefs(useNavStore())

const { mapRouteToListItem } = useNavStore()
const settingStore = useSettingStore()
const { isRouteLocked } = useSubscription()

const isRouteOffline = (route: RouteRecordRaw) => {
	return route?.meta?.offline === true
}

const getTourProps = (route: RouteRecordRaw) => {
	if (!route.meta?.tour) return null
	const nameFromRoute =
		typeof route.name === 'string' ? route.name : undefined
	const rawName: string | undefined = nameFromRoute ?? route.meta?.name
	if (!rawName) return null
	return {
		'data-tour': `nav${rawName.charAt(0).toUpperCase() + rawName.slice(1)}`,
	}
}

const navItemsFormatted = computed(() =>
	nav.value
		? mapRouteToListItem(
				nav.value.filter((items) => items.name !== 'administration'),
				(item, hasChildren) => {
					const getTo = (item: RouteRecordRaw) => {
						if (item?.redirect) return item.redirect
						if (item?.name) return { name: item.name }
						if (item?.path) return { path: item.path }
						return undefined
					}
					let data = {
						title: item.meta?.title ? t(item.meta?.title) : '',
						props: {
							prependIcon: item.meta?.icon,
							appendIcon:
								!hasChildren && isRouteLocked(item)
									? 'fa-solid fa-lock'
									: undefined,
							to: getTo(item),
							disabled:
								item === undefined ||
								isRouteLocked(item) ||
								(!isRouteOffline(item) && !isOnline.value),
						},
						meta: item.meta,
						'data-test': item.name ?? item.meta?.name,
					}

					const tourProp = getTourProps(item)
					if (tourProp) {
						data.props = { ...data.props, ...tourProp }
					}

					if (item.name === 'persons_group') {
						data.props.to =
							settingStore.getData.people_views > 0
								? getTo(item.children[1])
								: getTo(item.children[0])
					}

					if (item.name === 'documents_group') {
						data.props.to =
							settingStore.getData.documents_views > 0
								? getTo(item.children[1])
								: getTo(item.children[0])
					}

					return data
				}
			)
		: []
)

// Add this function to customize item props
const itemProps = (item: any) => {
	return {
		...item.props,
		'data-test': `nav-${item['data-test']}`,
	}
}

const mini = computed({
	get: () => cookies.get('mini'),
	set: (value) => cookies.set('mini', value),
})

const drawerTheme = computed(() =>
	useLightOrDark(theme.computedThemes.value.client.colors.nav)
		? 'light'
		: 'dark'
)

const onMouseEnter = () => {
	hover.value = true
}

const onMouseLeave = () => {
	hover.value = false
}

watch(
	() => display.name.value,
	(value) => {
		if (!isAuthenticated.value) return

		switch (value) {
			case 'xs':
				mini.value = false
				drawer.value = false
				break
			case 'sm':
				drawer.value = true
				mini.value = true
				break
			case 'md':
			case 'lg':
			case 'xl':
				drawer.value = true
				break
		}
	}
)

watch(route, () => {
	if (!isAuthenticated.value) return
	hover.value = false
})

watch(isAuthenticated, (value) => {
	drawer.value = value && display.mdAndUp.value
})
</script>

<style lang="scss" scoped>
#nav {
	overflow-x: hidden;
	overflow-y: auto;
}
</style>

<style scoped>
:deep(.v-list-group__items .v-list-item.v-list-item) {
	padding-inline-start: 1rem !important;
}
</style>
